import { type RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import { calculateTaxFactor, calculateTransferTaxFactor } from './index';
import {
  calculateEnhancedInterestRate,
  getBridgeRateByTerm,
} from './rateIntegration';

interface BridgeLoanParams {
  offerPrice?: number;
  listPrice: number;
  downPayment?: number;
  zipCode?: number;
  hoaFees?: number;
  commission?: number;
  monthlyPropertyTaxes?: number;
  monthlyHomeownersInsurance?: number;
  marketInterestRate?: number;
  creditScore?: number;
  loanTerm?: number; // In months: 6, 12, 18, or 24
}

export const calculateBridgeClosingDisclosure = (
  {
    offerPrice,
    listPrice,
    downPayment = 0,
    zipCode = 0,
    hoaFees = 0,
    commission = 0,
    monthlyPropertyTaxes: monthlyPropertyTaxesOverride,
    monthlyHomeownersInsurance: monthlyHomeownersInsuranceOverride,
    marketInterestRate = 10.5,
    creditScore = 0,
    loanTerm = 12, // Default 12 months
  }: BridgeLoanParams,
  state?: RootState
) => {
  const purchasePrice = offerPrice || listPrice;
  const loanAmount = purchasePrice - downPayment;
  const ltv = loanAmount / purchasePrice;

  const taxFactor = calculateTaxFactor(zipCode);
  const transferTaxFactor = calculateTransferTaxFactor(zipCode);

  // Get Bridge rate by term from Redux state
  const interestRate = state
    ? getBridgeRateByTerm(state, loanTerm, creditScore, ltv)
    : marketInterestRate; // Fallback to base rate if no state provided

  // Bridge loans are interest-only
  const monthlyPayment = (loanAmount * interestRate) / 1200; // Annual rate / 12 months
  const balloonPayment = loanAmount; // Full principal due at end

  // Constant fees for Bridge loans (higher than conventional)
  const appraisalFeeBase = 2500; // Higher appraisal fee
  const processingFeeBase = 1500; // Higher processing fee
  const underwritingFee = 2000; // Higher underwriting fee
  const applicationFeeBase = 1000; // Higher application fee
  const creditReportFeeBase = 200;
  const titleSettlementAgentFee = 1500;
  const countyRecordingFees = 200;
  const stateRecordingFees = 150;
  const recordingTitle = 75;
  const recordingMortgage = 75;
  const recordingDeedBase = 75;
  const floodMonitoringFee = 25;
  const floodDeterminationFee = 35;
  const pestInspectionFee = 145;
  const surveyFee = 600;
  const insuranceBinder = 150;
  const earnestMoneyDeposit = 1000;
  const lenderCredits = 0;
  const prepaidsPropertyTaxes = -100; // Credit from buyer

  // Bridge loan specific fees
  const bridgeFee = loanAmount * 0.02; // 2% bridge fee
  const exitFee = loanAmount * 0.01; // 1% exit fee

  // Factor-based calculations
  const annualPropertyTax = (purchasePrice * taxFactor) / 100;
  const monthlyPropertyTaxesBase = annualPropertyTax / 12;
  const monthlyPropertyTaxes =
    monthlyPropertyTaxesOverride ?? monthlyPropertyTaxesBase;

  // Title insurance
  const titleTitleSearchBase = 500;
  const titleLendersTitleInsuranceBase = purchasePrice * 0.0065;
  const optionalOwnersTitleInsuranceBase = purchasePrice * 0.00575;
  const minimumOwnersTitle = 1650;
  const maximumOwnersTitle = 5850;
  const optionalOwnersTitleInsurance = Math.min(
    Math.max(optionalOwnersTitleInsuranceBase, minimumOwnersTitle),
    maximumOwnersTitle
  );

  // Insurance and prepaids
  const monthlyHomeownersInsuranceBase = (purchasePrice * 0.01035) / 12;
  const monthlyHomeownersInsurance =
    monthlyHomeownersInsuranceOverride ?? monthlyHomeownersInsuranceBase;
  const prepaidsHomeownersInsurance = monthlyHomeownersInsurance * 3;
  const initialEscrowTaxes = monthlyPropertyTaxes * 6;

  // Prepaid interest (15 days)
  const dailyInterestRate = interestRate / 365 / 100;
  const prepaidInterest = loanAmount * dailyInterestRate * 15;

  // Transfer taxes
  const transferTaxes = (purchasePrice * transferTaxFactor) / 100;

  // Calculate totals
  const totalOriginationFees =
    underwritingFee +
    applicationFeeBase +
    processingFeeBase +
    creditReportFeeBase +
    bridgeFee;

  const totalOtherFees =
    appraisalFeeBase +
    titleSettlementAgentFee +
    floodDeterminationFee +
    floodMonitoringFee +
    pestInspectionFee +
    surveyFee +
    countyRecordingFees +
    stateRecordingFees +
    transferTaxes +
    prepaidInterest +
    prepaidsHomeownersInsurance +
    initialEscrowTaxes +
    optionalOwnersTitleInsurance +
    insuranceBinder +
    titleTitleSearchBase +
    titleLendersTitleInsuranceBase +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase +
    exitFee;

  const totalLenderCosts = totalOriginationFees + totalOtherFees;

  // Total interest
  const numberOfPayments = loanTerm;
  const totalInterest = monthlyPayment * loanTerm;

  // Total cost
  const totalCost = totalInterest + totalLenderCosts;

  // Cash to close
  const cashToClose = downPayment + totalLenderCosts;

  // Monthly PITI
  const piti =
    monthlyPayment +
    monthlyPropertyTaxes +
    monthlyHomeownersInsurance +
    hoaFees;

  return {
    type: USER_LOAN_PROGRAM.BRIDGE,
    offerPrice: offerPrice || undefined,
    listPrice,
    purchasePrice,
    commission,
    marketInterestRate: interestRate,
    creditScore,
    loanAmount,
    interestRate,
    monthlyPayment,
    monthlyPrincipalInterest: monthlyPayment,
    loanTerm, // Return in months
    balloonPayment,
    numberOfPayments: loanTerm,
    totalInterest,
    totalCost,
    cashToClose,
    piti,
    // Origination fees
    underwritingFee,
    applicationFeeBase,
    processingFeeBase,
    creditReportFeeBase,
    bridgeFee,
    // Other fees
    appraisalFeeBase,
    titleSettlementAgentFee,
    floodDeterminationFee,
    floodMonitoringFee,
    pestInspectionFee,
    surveyFee,
    countyRecordingFees,
    stateRecordingFees,
    transferTaxes,
    prepaidInterest,
    prepaidsHomeownersInsurance,
    initialEscrowTaxes,
    optionalOwnersTitleInsurance,
    insuranceBinder,
    titleTitleSearchBase,
    titleLendersTitleInsuranceBase,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    exitFee,
    // Totals
    totalOriginationFees,
    totalOtherFees,
    totalLenderCosts,
    // Additional fields
    monthlyPropertyTaxes,
    monthlyHomeownersInsurance,
    earnestMoneyDeposit,
    lenderCredits,
    prepaidsPropertyTaxes,
    initialEscrowMortgageInsurance: 0, // Not applicable to Bridge loans
    // Loan type specific
    loanProgram: USER_LOAN_PROGRAM.BRIDGE,
  };
};
